// -----------------------------------------------------------------------------
// Legal & regulatory documents for The TMD Physio.
//
// Each entry powers one page rendered by `<LegalPage>` at `/legal/{slug}`.
// Content is structured as an array of sections so the page can be updated
// without touching JSX. A section may optionally carry a `trustLogos` array
// (see Regulatory Details § 2 for the Hiscox example) or a `draftNote` if a
// policy needs a public "under review" banner.
// -----------------------------------------------------------------------------

const CLINIC_NAME = "The TMD Physio";
// Swapped for the live Practice Details address by LegalPage at render.
export const PRACTICE_ADDRESS_TOKEN = "{{PRACTICE_ADDRESS}}";
const CLINIC_ADDRESS = PRACTICE_ADDRESS_TOKEN;
const CLINIC_EMAIL = "secretary@krinapanchalphysio.com";
const CLINIC_PHONE = "+44 7899 121 612";

// Last-updated defaults: use today's date for all new policies. Individual
// docs can override if they've been reviewed on a later date.
const TODAY = "1 February 2026";

export const legals = [
  {
    slug: "terms-and-conditions",
    title: "Terms and Conditions",
    metaDescription:
      `Terms and Conditions governing use of physiotherapy services provided by The TMD Physio at ${CLINIC_ADDRESS}.`,
    lastUpdated: TODAY,
    sections: [
      {
        heading: "1. About these Terms",
        body: `These Terms and Conditions govern your use of services provided by ${CLINIC_NAME} at ${CLINIC_ADDRESS}, and any content on our website www.krinapanchalphysio.com. By booking an appointment or using our services you agree to these Terms.`,
      },
      {
        heading: "2. Who we are",
        body: `${CLINIC_NAME} is a specialist private physiotherapy clinic operated by Krina Panchal (BSc Hons Physiotherapy, MCSP, HCPC-registered — registration number PH95601). We specialise in the assessment and treatment of temporomandibular disorders (TMD), chronic headaches, migraines, tinnitus and facial pain.`,
      },
      {
        heading: "3. Our services",
        body: `We offer:
• In-person physiotherapy assessments and treatment sessions at ${CLINIC_ADDRESS}
• Virtual consultations by secure video call
• Educational programmes (including "Why Do I Have TMD?")
• Group masterclasses and workshops
• Gift vouchers, treatment blocks and monthly retainer packages
The scope of what is clinically appropriate is decided by Krina at your first assessment and may change during care.`,
      },
      {
        heading: "4. Booking, fees and payment",
        body: `Fees are published on our Fees page (www.krinapanchalphysio.com/fees) and may be updated with 30 days' notice. Payment is due at the end of each session unless a pre-paid block or subscription has been purchased. We accept card payments, bank transfer, and are recognised by selected private medical insurers. For our cancellation terms, see our Booking and Cancellation Policy.`,
      },
      {
        heading: "5. Clinical consent and safety",
        body: `Physiotherapy involves physical examination and hands-on treatment. Before your first appointment we will explain the assessment and treatment options and obtain your informed consent. You may withdraw consent at any time, verbally or in writing. If we identify a condition that falls outside our clinical scope, we will refer you back to your GP, consultant or a more appropriate specialist.`,
      },
      {
        heading: "6. Medical disclaimer",
        body: `Healthcare begins and ends with you. That is to say, as your needs for healthcare and healthcare information change, you must evaluate not only the source but also what is provided. It is your right to be thoroughly informed; but as importantly, it is your responsibility to participate actively in your own care and to participate in the decision-making process.

The information provided on this website is not intended to replace consultation, evaluation, services or the establishment of a licensed practitioner-patient relationship. It is not diagnostic and does not replace an in-person examination and an agreed course of action. It is not being used, or to be implied, as a means of practising outside of the state or scope of licensure. The material is meant for information and possible supplementation of what is provided by your specific healthcare provider. It should be reviewed with that person as well.

${CLINIC_NAME} specifically disclaims all responsibility for any liability, loss or risk, personal or otherwise, which is incurred as a result — directly or indirectly — of the use and application of any material on this site. The information and/or advice on this site is not intended to replace medical care.`,
      },
      {
        heading: "7. Website use and intellectual property",
        body: `Our website content is provided for general information only and does not constitute medical advice. You should not rely on website content as a substitute for professional physiotherapy assessment. All website content — articles, images, videos, downloadable resources and educational programmes — remains the intellectual property of ${CLINIC_NAME} and Krina Panchal. You may share links freely but must not reproduce content commercially without written permission.`,
      },
      {
        heading: "8. Limitation of liability",
        body: `Nothing in these Terms excludes liability for death or personal injury caused by our negligence, for fraudulent misrepresentation, or for any liability that cannot be excluded under English law. Subject to that, our total liability to you for any claim arising from our services is limited to the total fees you have paid to us in the 12 months preceding the claim. We are not liable for indirect or consequential loss.`,
      },
      {
        heading: "9. Governing law",
        body: `These Terms are governed by the laws of England and Wales. The English courts have exclusive jurisdiction to hear any dispute arising in connection with these Terms.`,
      },
      {
        heading: "10. Changes to these Terms",
        body: `We may update these Terms from time to time. The version published on this page on the date you book an appointment is the version that applies to that appointment.`,
      },
      {
        heading: "11. Contact",
        body: `Questions about these Terms: ${CLINIC_EMAIL} or ${CLINIC_PHONE}.`,
      },
    ],
  },

  {
    slug: "cookie-policy",
    title: "Cookie Policy",
    metaDescription:
      "How The TMD Physio uses cookies on krinapanchalphysio.com, categories of cookies, third-party services and how to change your consent.",
    lastUpdated: TODAY,
    sections: [
      {
        heading: "1. What are cookies?",
        body: `Cookies are small text files stored on your device when you visit a website. They allow the site to remember your actions, preferences and analytics across visits — for example, remembering that you have accepted our cookie banner so we don't ask you again on every page.`,
      },
      {
        heading: "2. Categories of cookies we use",
        body: `We use only two categories of cookies:

Strictly Necessary — Always on. Required to make the site work: login/security, session state, and your cookie-consent choice itself. Legal basis: legitimate interest. Retention: session or up to 12 months for your consent choice.

Analytics & Marketing — Off by default. Only active if you click "Accept all cookies" on our consent banner. Includes Google Analytics 4 (site usage), Meta Pixel (Instagram/Facebook attribution), and Google Ads conversion tracking. Legal basis: consent. Retention: up to 24 months.`,
      },
      {
        heading: "3. Third-party services that may set cookies",
        body: `Subject to your consent choice, cookies may be set by:
• Google — Analytics, Ads and reCAPTCHA
• Meta (Instagram/Facebook) — Pixel and embedded content
• Stripe — payment processing (cookies set only during checkout, under legitimate interest)
• Cloudflare — bot protection (strictly necessary)
Each provider maintains its own privacy documentation which you can review directly on their websites.`,
      },
      {
        heading: "4. Your choices",
        body: `You can:
• Change your consent at any time via the "Cookie preferences" button in our footer — this reopens the consent banner
• Delete cookies via your browser settings (Chrome, Safari, Firefox and Edge each have their own path — see aboutcookies.org for step-by-step guides)
• Enable "Do Not Track" in your browser — we respect this signal and treat it as an implicit rejection of analytics/marketing cookies`,
      },
      {
        heading: "5. Contact",
        body: `Questions about cookies: ${CLINIC_EMAIL}. Data-protection concerns can also be raised with the ICO (ico.org.uk / 0303 123 1113).`,
      },
    ],
  },

  {
    slug: "privacy-notice",
    title: "Privacy Notice",
    metaDescription:
      "How The TMD Physio collects, uses and protects your personal and clinical data under UK GDPR and the Data Protection Act 2018.",
    lastUpdated: TODAY,
    sections: [
      {
        heading: "1. Who we are",
        body: `${CLINIC_NAME} is the data controller under the UK GDPR and Data Protection Act 2018. Registered address: ${CLINIC_ADDRESS}. Data protection contact: Krina Panchal (${CLINIC_EMAIL}). ICO registration number: ZB288125.`,
      },
      {
        heading: "2. What data we collect",
        body: `We collect four categories of information:

Contact data — name, email, phone, address, referrer details (from booking or referral forms).

Clinical data — health history, symptoms, examination findings, treatment notes, any imaging you share with us, correspondence with your GP or consultant, and outcome measures collected during your care.

Payment data — processed by Stripe. We do not store your full card details on our systems.

Website usage data — analytics and cookies as detailed in our Cookie Policy.`,
      },
      {
        heading: "3. Why we collect it (lawful bases under UK GDPR)",
        body: `Provision of healthcare — Article 6(1)(b) contract + Article 9(2)(h) health provision. Applies to your clinical records, appointment scheduling, and consultant/insurer correspondence.

Marketing communications — Article 6(1)(a) consent. Applies only where you have opted in. Withdrawable at any time via the unsubscribe link in every marketing email.

Legal or regulatory compliance — Article 6(1)(c). Applies to HMRC records, HCPC/CSP investigations, and lawful requests from public authorities.`,
      },
      {
        heading: "4. Who we share your data with",
        body: `We share data on a strict need-to-know basis with:
• Your GP or referring consultant — only with your explicit consent for a summary letter after appointments
• Your private medical insurer — for claim processing, only after you sign an authorisation
• Our IT service providers (email, secure appointment booking, cloud storage) — all UK/EU-hosted and GDPR-compliant, bound by contractual data-processor terms
• HMRC or public authorities where required by law
We do not sell your data to anyone, ever.`,
      },
      {
        heading: "5. How long we keep it",
        body: `Clinical records — 8 years from your last appointment (or, for children, until age 25, whichever is longer). This aligns with Chartered Society of Physiotherapy retention guidance.

Contact and marketing data — until you unsubscribe or ask for deletion. Deleted within 30 days of your request.

Payment records — 7 years, as required by HMRC.

Website analytics — a maximum of 24 months, then automatically aggregated or deleted.`,
      },
      {
        heading: "6. Your rights",
        body: `Under UK GDPR you have the right to:
• Access your data (Subject Access Request)
• Rectify inaccurate data
• Erase your data (subject to the clinical retention requirements above)
• Restrict or object to processing
• Data portability — receive your records in a machine-readable format
• Withdraw consent for marketing at any time
• Complain to the ICO — ico.org.uk / 0303 123 1113
Requests should be sent to ${CLINIC_EMAIL}. We respond within 30 days.`,
      },
      {
        heading: "7. International transfers",
        body: `Some of our IT service providers store data in the EU or under UK-approved international transfer safeguards (Standard Contractual Clauses). We do not transfer clinical data outside the UK/EU without your explicit consent.`,
      },
      {
        heading: "8. Security",
        body: `We protect your data using access controls, encrypted storage, TLS on all connections to our website, MFA on administrative accounts, and regular security reviews. In the unlikely event of a personal-data breach affecting your rights, we will notify you and the ICO within 72 hours as required by law.`,
      },
      {
        heading: "9. Contact",
        body: `Any privacy question or rights request: ${CLINIC_EMAIL}. To make a complaint to the regulator: ico.org.uk / 0303 123 1113.`,
      },
    ],
  },

  {
    slug: "complaints-policy",
    title: "Complaints Policy",
    metaDescription:
      "How to raise a complaint or concern about care received at The TMD Physio, our timeline for response, and how to escalate to CSP, HCPC or the ICO.",
    lastUpdated: TODAY,
    sections: [
      {
        heading: "1. Our commitment",
        body: `We take complaints seriously. Every concern is an opportunity to improve. If you are unhappy with any aspect of your care, please tell us — we would much rather hear it from you first than not at all. Raising a concern will never affect the care you receive.`,
      },
      {
        heading: "2. How to raise a complaint",
        body: `Step 1 — Informal resolution. In the first instance please speak to Krina directly at your appointment, by phone (${CLINIC_PHONE}) or by email (${CLINIC_EMAIL}). The vast majority of concerns are resolved this way.

Step 2 — Formal complaint. If informal discussion doesn't resolve the concern, please put your complaint in writing to:
${CLINIC_NAME}
Krina Panchal
${CLINIC_ADDRESS}
${CLINIC_EMAIL}
Please include: your name, dates of appointment(s), a clear description of your concern, and the outcome you are seeking.`,
      },
      {
        heading: "3. Our timeline",
        body: `Within 3 working days — we acknowledge receipt of your written complaint.

Within 10 working days — we complete an internal investigation and provide a written response.

If more time is needed — we tell you why and give a revised timeline. We will never take longer than 20 working days from acknowledgement without agreeing an extension with you.`,
      },
      {
        heading: "4. External escalation",
        body: `If you remain unhappy after our response, you can escalate to:

Health and Care Professions Council (HCPC) — the statutory regulator for physiotherapists. hcpc-uk.org / 0300 500 6184. Krina's HCPC registration can be verified at hcpc-uk.org/check-the-register.

Chartered Society of Physiotherapy (CSP) — the professional body for physiotherapists. csp.org.uk / 020 7306 6666.

Information Commissioner's Office (ICO) — for data-protection concerns only. ico.org.uk / 0303 123 1113.

Independent Sector Complaints Adjudication Service (ISCAS) — for private healthcare complaints where relevant. iscas.cedr.com.`,
      },
      {
        heading: "5. Your rights during a complaint",
        body: `Making a complaint will never affect the care you receive. You may raise concerns with any of the regulators above in parallel with (or instead of) our internal process — you do not need to complete our internal process first. You may bring a supporter or advocate to any meeting we hold about your complaint.`,
      },
      {
        heading: "6. Learning from complaints",
        body: `Every complaint is logged and reviewed. Themes identified across complaints inform our ongoing service improvements. Where a complaint reveals a systemic issue, we will act on it and — where you consent — write back to you specifically to describe what has changed.`,
      },
    ],
  },

  {
    slug: "booking-and-cancellation-policy",
    title: "Booking and Cancellation Policy",
    metaDescription:
      "How to book, reschedule and cancel appointments at The TMD Physio. 48-hour cancellation notice, arrival guidance, forms and accessibility information.",
    lastUpdated: TODAY,
    sections: [
      {
        heading: "1. How to book",
        body: `You can book an appointment:
• Online via the "Book Now" button on our website or via the enquiry form on our Contact page
• By phone on ${CLINIC_PHONE}
• By WhatsApp on the same number
• By email to ${CLINIC_EMAIL}
We aim to respond to all booking enquiries within 24 hours on Monday to Friday, or by the next working day if received over a weekend or bank holiday.`,
      },
      {
        heading: "2. Confirmation and reminders",
        body: `Once your appointment is confirmed, you will receive an email confirmation containing the date, time, clinic address (${CLINIC_ADDRESS}) and pre-appointment instructions. An automated reminder is sent 24 hours before your appointment by email (or SMS if you have opted in).`,
      },
      {
        heading: "3. Cancellation policy",
        body: `There is a strict 48-hour cancellation policy on all appointments. A full charge (100% of the appointment fee) will be taken for any cancellation made less than 48 hours before the appointment, and for any missed appointment.

If you wish to cancel or reschedule, please contact us as early as possible by email, phone or WhatsApp — the 48-hour window is measured from the time we receive your notice, not the time it was sent.`,
      },
      {
        heading: "4. Illness and exceptional circumstances",
        body: `If you are unwell — particularly with a contagious illness such as flu, COVID-19 or a stomach bug — please do not attend the clinic. Call or message us as soon as you can. Bereavement, family emergency and major travel disruption are considered on a case-by-case basis; please contact us directly.`,
      },
      {
        heading: "5. Prior to your initial assessment",
        body: `You will need to fill out some pre-appointment forms prior to your first appointment. Please have them completed before you arrive.

If you decide to fill them out during the appointment an extra fee will be charged, as this will reduce the clinical time available for assessment and treatment.

If any part of the form is left incomplete and Krina has to spend appointment time asking additional questions to fill the gaps, an extra fee will be charged.

If you are unable to print your forms yourself, please let us know at least 48 hours before your appointment and we can arrange printing at the clinic. In that case you must arrive at least 30 minutes before your appointment to fill them out in the reception area.`,
      },
      {
        heading: "6. Facilities, arrival and access",
        body: `Unfortunately the clinic does not currently have step-free / disabled access. If you have specific accessibility requirements please contact us before booking so we can discuss options with you.

The clinic is shared with other health professionals. Please be respectful of other patients and practitioners while you are in the building.

Please arrive 10 minutes before your appointment — no earlier. Due to the size of the clinic and the volume of patients passing through, we cannot accommodate patients who arrive more than 10 minutes early. If you arrive earlier, there are many coffee shops nearby around Waterloo and Lower Marsh where you are welcome to wait comfortably.

Please let the receptionist know you have arrived for an appointment with Krina. Krina will come and collect you personally once she is ready.`,
      },
      {
        heading: "7. Medical concerns",
        body: `All medical concerns must be highlighted during your initial appointment so Krina can incorporate them into your assessment and treatment plan.

If there is any change in your medical condition — for example a new diagnosis, a new medication, pregnancy, or a recent hospital admission — between appointments, please notify us as soon as possible so your care can be adjusted safely.`,
      },
      {
        heading: "8. Late arrival",
        body: `If you arrive more than 15 minutes late we may need to shorten your session to protect the following patient's appointment; the full session fee remains payable.`,
      },
      {
        heading: "9. If we need to cancel",
        body: `On rare occasions (illness, unavoidable emergency) Krina may need to cancel a booked appointment. We will contact you as early as possible and either reschedule at your convenience or refund any pre-paid fees in full within 5 working days.`,
      },
      {
        heading: "10. Insurance and third-party bookings",
        body: `If your appointment is being paid via a private medical insurer, the same 48-hour cancellation policy applies. Some insurers do not cover cancellation fees, in which case any late-cancellation or no-show fee will be charged to you personally.`,
      },
      {
        heading: "11. Prepaid blocks, subscriptions and gift vouchers",
        body: `For treatment blocks, monthly retainer subscriptions and gift vouchers, please see our Terms and Conditions and the Fees page. Blocks and subscriptions have their own expiry and refund rules which supersede this policy where they conflict.`,
      },
      {
        heading: "12. Acceptance",
        body: `By booking an appointment with ${CLINIC_NAME} you accept the terms set out above. This policy applies from your first appointment onwards.`,
      },
    ],
  },

  {
    slug: "regulatory-details",
    title: "Regulatory Details",
    metaDescription:
      "Regulatory and professional-body registrations, insurance, and complaints escalation for The TMD Physio and Krina Panchal.",
    lastUpdated: TODAY,
    sections: [
      {
        heading: "1. The clinician",
        body: `Name: Krina Panchal
Qualifications: BSc (Hons) Physiotherapy, BSc (Hons) Biomedical Science

Registrations:
• Chartered Society of Physiotherapy (CSP) — MCSP. Membership number: 087384
• Health and Care Professions Council (HCPC) — Registrant. Registration number: PH95601. Verify at hcpc-uk.org/check-the-register

Postgraduate training:
• Masters Module Assessment and Management of TMD (University of Liverpool)
• Steve Kraus TMD Course (Atlanta, 2014)
• CRAFTA® (Italy, 2016)
• Watson Headache® Foundation (2017)
• Instituto Rocabado BOSCO TMD (2017)
• IFM Applying Functional Medicine in Clinical Practice (Cohort 108)

Special interests: temporomandibular disorders, chronic headache, migraine, somatic tinnitus and facial pain.`,
      },
      {
        heading: "2. The business",
        body: `Trading name: ${CLINIC_NAME}
Registered address: ${CLINIC_ADDRESS}
Contact: ${CLINIC_EMAIL} / ${CLINIC_PHONE}
ICO registration (data protection): ZB288125
Professional indemnity insurance: held with Hiscox Insurance Company Limited (UK), one of the leading specialist insurers for allied health professionals. Cover extends to the full scope of physiotherapy practice, including clinic-based and (where applicable) home-visit treatment. Policy certificate available on request.`,
        trustLogos: [
          {
            src: "/logos/insurers/hiscox.svg",
            alt: "Hiscox — professional indemnity insurer",
            name: "Hiscox",
            caption: "Professional indemnity underwriter",
          },
        ],
      },
      {
        heading: "3. Regulatory oversight",
        body: `As a Chartered Physiotherapist practising in the UK, Krina is subject to the following regulatory oversight:

Health and Care Professions Council (HCPC) — statutory regulator. Sets and maintains the standards required to practise as a physiotherapist. Complaints can be made directly to HCPC via hcpc-uk.org or 0300 500 6184.

Chartered Society of Physiotherapy (CSP) — professional body. Sets member standards, provides indemnity cover, and represents the physiotherapy profession.

Information Commissioner's Office (ICO) — data-protection regulator. Complaints via ico.org.uk / 0303 123 1113.`,
      },
      {
        heading: "4. Insurance recognition",
        body: `We are recognised providers for the following UK private medical insurers:
• Cigna
• WPA
• Bupa Global
• Healix Health

We are NOT currently recognised providers for Bupa (UK), AXA PPP Healthcare, Aviva or Vitality. If you are covered by one of these insurers we can still issue detailed self-pay invoices — many patients reclaim via their plan's out-of-network route. Please check with your insurer before booking that your specific plan covers physiotherapy for your presenting concern.`,
      },
      {
        heading: "5. Advertising standards",
        body: `All claims on this website comply with the Advertising Standards Authority (ASA) CAP Code for healthcare marketing and the Chartered Society of Physiotherapy's guidance on responsible clinical advertising. If you believe any specific claim is misleading, please contact us at ${CLINIC_EMAIL} — we take this seriously and will respond promptly.`,
      },
      {
        heading: "6. Complaints",
        body: `See our Complaints Policy for full details on how to raise a concern about care and how to escalate it externally.`,
      },
    ],
  },
];

// Menu items powering the footer Legal & Policies dropdown. Order = display
// order (users expect Terms, Privacy, Cookies at the top of a legal menu).
export const legalMenuOrder = [
  "terms-and-conditions",
  "privacy-notice",
  "cookie-policy",
  "booking-and-cancellation-policy",
  "complaints-policy",
  "regulatory-details",
];

export const legalsBySlug = Object.fromEntries(legals.map((l) => [l.slug, l]));
